import { Component, OnDestroy, inject, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

/** Wait this long before showing, so instant navigations never flash the loader. */
const SHOW_DELAY = 140;
/** Once shown, keep it up long enough to read. */
const MIN_VISIBLE = 280;
/** The first paint keeps the splash up briefly, then hands over to the app. */
const INITIAL_MIN_VISIBLE = 260;

/**
 * The app-wide loading veil. It listens to route changes and appears only when
 * a navigation takes long enough to notice, with the PGC mark breathing in the
 * middle and a single indeterminate line underneath.
 */
@Component({
  selector: 'app-global-loader',
  template: `
    <div
      class="loader"
      [class.visible]="visible()"
      [attr.aria-hidden]="visible() ? null : 'true'"
      role="status"
      aria-live="polite"
    >
      <div class="loader-panel">
        <div class="logo-stage" aria-hidden="true">
          <span class="orbit"></span>
          <span class="logo-tile"><img src="pgc-logo.png" alt="" /></span>
        </div>
        <div class="loader-copy">
          <span class="eyebrow">EBP </span>
          <span class="title">Preparing your workspace</span>
        </div>
        <span class="progress" aria-hidden="true"><span></span></span>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: contents;
    }

    .loader {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: grid;
      place-items: center;
      background:
        radial-gradient(
          560px 420px at 20% 16%,
          color-mix(in srgb, var(--mat-sys-primary) 10%, transparent),
          transparent 66%
        ),
        radial-gradient(
          620px 460px at 82% 86%,
          color-mix(in srgb, var(--mat-sys-tertiary) 11%, transparent),
          transparent 68%
        ),
        linear-gradient(160deg, #f8faff 0%, #eef2fb 56%, #f7f3ff 100%);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition:
        opacity 220ms ease,
        visibility 220ms ease;

      &.visible {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }
    }

    .loader-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 2rem;
      text-align: center;
      animation: panel-in 260ms ease both;
    }

    .logo-stage {
      position: relative;
      display: grid;
      width: 116px;
      height: 116px;
      place-items: center;

      // A quiet halo behind the mark.
      &::before {
        content: '';
        position: absolute;
        inset: 4px;
        border-radius: 50%;
        background: radial-gradient(
          circle,
          color-mix(in srgb, var(--mat-sys-primary) 17%, transparent),
          transparent 68%
        );
        animation: halo 2.8s ease-in-out infinite;
      }

      // A ring that softly expands and fades.
      .orbit {
        position: absolute;
        inset: 0;
        border: 1px solid color-mix(in srgb, var(--mat-sys-primary) 22%, transparent);
        border-radius: 50%;
        animation: orbit 2.8s ease-out infinite;
      }
    }

    .logo-tile {
      position: relative;
      display: grid;
      width: 88px;
      height: 92px;
      place-items: center;
      padding: 10px;
      border-radius: 26px;
      background: rgba(255, 255, 255, 0.94);
      box-shadow:
        0 22px 44px -22px color-mix(in srgb, var(--mat-sys-primary) 65%, transparent),
        inset 0 0 0 1px rgba(255, 255, 255, 0.9);
      animation: float 2.8s ease-in-out infinite;

      img {
        display: block;
        max-width: 58px;
        max-height: 62px;
        object-fit: contain;
      }
    }

    .loader-copy {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.3rem;
      margin-top: 1.25rem;
    }

    .eyebrow {
      color: var(--mat-sys-primary);
      font: var(--mat-sys-label-small);
      font-weight: 750;
      letter-spacing: 0.16em;
      text-transform: uppercase;
    }

    .title {
      color: var(--mat-sys-on-surface);
      font: var(--mat-sys-title-medium);
      font-weight: 600;
    }

    .progress {
      position: relative;
      width: 164px;
      height: 4px;
      margin-top: 1.35rem;
      overflow: hidden;
      border-radius: var(--mat-sys-corner-full);
      background: color-mix(in srgb, var(--mat-sys-primary) 11%, var(--mat-sys-surface));

      span {
        position: absolute;
        top: 0;
        bottom: 0;
        left: -45%;
        width: 45%;
        border-radius: inherit;
        background: linear-gradient(
          90deg,
          transparent,
          var(--mat-sys-primary) 35%,
          var(--mat-sys-tertiary) 70%,
          transparent
        );
        animation: progress 1.4s ease-in-out infinite;
      }
    }

    @keyframes panel-in {
      from {
        opacity: 0;
        transform: translateY(6px);
      }
      to {
        opacity: 1;
        transform: none;
      }
    }

    @keyframes float {
      0%,
      100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-5px);
      }
    }

    @keyframes halo {
      0%,
      100% {
        opacity: 0.55;
        transform: scale(0.94);
      }
      50% {
        opacity: 1;
        transform: scale(1.06);
      }
    }

    @keyframes orbit {
      0% {
        opacity: 0.6;
        transform: scale(0.82);
      }
      70%,
      100% {
        opacity: 0;
        transform: scale(1.16);
      }
    }

    @keyframes progress {
      0% {
        left: -45%;
      }
      100% {
        left: 105%;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .loader-panel,
      .logo-tile,
      .logo-stage::before,
      .logo-stage .orbit,
      .progress span {
        animation: none;
      }

      .progress span {
        left: 0;
        width: 100%;
        opacity: 0.65;
      }
    }
  `,
})
export class GlobalLoaderComponent implements OnDestroy {
  private readonly router = inject(Router);
  private showTimer?: ReturnType<typeof setTimeout>;
  private hideTimer?: ReturnType<typeof setTimeout>;
  private safetyTimer?: ReturnType<typeof setTimeout>;
  private shownAt = Date.now();
  private initial = true;

  // The CSS splash in index.html is visible until Angular boots. Starting
  // visible here makes the hand-over seamless instead of flashing the page.
  readonly visible = signal(true);

  constructor() {
    this.safetyTimer = setTimeout(() => {
      if (this.initial) {
        this.visible.set(false);
        this.initial = false;
      }
    }, 6000);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.schedule();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.finish();
      }
    });

    // A fast initial navigation may have finished before this component existed.
    if (this.router.navigated) this.finish();
  }

  ngOnDestroy(): void {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    clearTimeout(this.safetyTimer);
  }

  private schedule(): void {
    clearTimeout(this.hideTimer);
    if (this.visible()) return;

    clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => {
      this.shownAt = Date.now();
      this.visible.set(true);
    }, SHOW_DELAY);
  }

  private finish(): void {
    clearTimeout(this.showTimer);
    if (!this.visible()) {
      this.initial = false;
      return;
    }

    const minimum = this.initial ? INITIAL_MIN_VISIBLE : MIN_VISIBLE;
    const remaining = Math.max(0, minimum - (Date.now() - this.shownAt));
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.visible.set(false);
      this.initial = false;
    }, remaining);
  }
}
